// ============================================================================
// LE FIL D'ÉTAPES D'UN ASSISTANT — où l'on est, et ce qu'il reste.
//
// ⚠️ IL MONTRE LE PLAN, PAS SEULEMENT L'AVANCEMENT. Les étapes à venir restent LISIBLES (elles ne sont
// simplement pas cliquables) : c'est ce qui permet de décider si l'on s'engage dans un assistant de six
// écrans avant d'y entrer. Les masquer transformerait le fil en barre de progression, qui ne répond
// qu'à « où en suis-je » et jamais à « où va-t-on ».
//
// ⚠️ ON NE REVIENT QU'EN ARRIÈRE. Sauter vers une étape à venir laisserait la traverser sans remplir
// celles d'avant — et les assistants du dépôt construisent chacun sur le précédent (la matrice d'une
// gamme vient des tailles, ses codes viennent de la matrice). L'avant se corrige, l'après se gagne.
//
// ⚠️ LE TRAIT SE COLORE DERRIÈRE, pas devant : il dit le chemin PARCOURU. Coloré en avant, il
// promettrait un travail qui n'est pas fait.
// ============================================================================
import { OZ_INTERACTIVE } from './interactive';

export interface StepsProps {
  /** Les libellés, dans l'ordre. Le rang affiché est leur position (1-based). */
  items: readonly string[];
  /** L'étape courante, 1-based. */
  current: number;
  /** Aller à une étape — appelé UNIQUEMENT pour une étape déjà franchie. */
  onGo: (step: number) => void;
  /** Décrit l'assistant pour un lecteur d'écran (« Étapes de la création d'une gamme »). */
  label: string;
  /**
   * La couleur du chemin parcouru — celle du TON de l'assistant (la gamme en sarcelle, l'import en bleu : la maquette
   * accorde le fil à la tuile de sa modale).
   */
  tone?: 'teal' | 'blue';
  /** Sans marge ni filet : posé dans la bande fixe d'une modale (`Modal.band`), qui les porte déjà. */
  bare?: boolean;
}

export function Steps({ items, current, onGo, label, tone = 'teal', bare = false }: StepsProps) {
  const ink = tone === 'blue' ? 'var(--blue)' : 'var(--teal-500)';
  return (
    <ol
      aria-label={label}
      style={{
        display: 'flex', alignItems: 'center', padding: bare ? 0 : '14px 0', margin: 0, listStyle: 'none',
        borderBottom: bare ? 'none' : '1px solid var(--ink-100)',
        // ⚠️ LES DEUX AXES ENSEMBLE : `overflow-x: auto` seul fait passer l'autre en `auto` (spec CSS),
        // et un pixel de dépassement y ferait apparaître un ascenseur vertical sur une seule rangée.
        overflowX: 'auto', overflowY: 'hidden',
      }}
    >
      {items.map((libelle, i) => {
        const n = i + 1;
        const atteinte = current >= n;
        const passee = current > n;
        return (
          <li key={libelle} style={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
            <button
              type="button"
              className={OZ_INTERACTIVE}
              onClick={() => { if (n < current) onGo(n); }}
              aria-disabled={n >= current || undefined}
              aria-current={n === current ? 'step' : undefined}
              style={{
                display: 'flex', alignItems: 'center', gap: 7, border: 'none', background: 'transparent',
                padding: 0, minWidth: 0, cursor: n < current ? 'pointer' : 'default',
              }}
            >
              <span style={{
                width: 22, height: 22, borderRadius: 'var(--r-pill)', flexShrink: 0,
                background: atteinte ? ink : 'var(--ink-200)',
                color: atteinte ? '#fff' : 'var(--ink-400)',
                fontSize: 'var(--t-meta)', fontWeight: 700, display: 'flex', alignItems: 'center',
                justifyContent: 'center', fontVariantNumeric: 'tabular-nums',
              }}>{n}</span>
              <span style={{
                fontSize: 'var(--t-dense)', fontWeight: 600, color: 'var(--ink-700)',
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              }}>{libelle}</span>
            </button>
            {n < items.length && (
              <span aria-hidden style={{
                flex: 1, height: 2, minWidth: 8, margin: '0 8px', borderRadius: 'var(--r-pill)',
                background: passee ? ink : 'var(--ink-200)',
              }} />
            )}
          </li>
        );
      })}
    </ol>
  );
}
